// S5 · Step 3 — the plant tree + properties panel (Module 02 §4 S5), rebuilt on
// the design-system §3.7 structure-editor grammar (UX-1, D-107): a dense tree
// of rows (facility → department → work center, plus per-facility storage
// locations), ONE selected node, and a right-hand PropertiesPanel that edits
// the selection. The BEHAVIOR is unchanged — the same immutable draft value,
// the same field ids (`${uid}-code`) the problems panel focuses, the same
// Move up / Move down buttons (reordering must work on a phone and with a
// keyboard; drag is neither — §4 S5), the same §3.4 remove-with-children
// confirmation and undo.
//
// Draft chips: a node whose code has no committed match carries the accent
// `draft` chip — the tree itself answers "what exists vs what is only typed",
// which is the comprehension the owner asked for ("no clue if the mapping
// works"). Committed rows carry the REAL entity id as `data-node-id`, which is
// what the state-4 banner links resolve to in the walkthrough.
//
// Entity nouns come from `useTerm()` (§2.5), never from a literal.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTerm } from "../../hooks/terminology";
import {
  PropertiesPanel,
  TreeGhostRow,
  TreeNodeRow,
  TreeRowAction,
} from "../../../../components/structure";
import { Button } from "../../../../components/button";
import { WizardDialog } from "../../components/wizard-dialog";
import {
  bulkWorkCenters,
  departmentSubtreeCounts,
  emptyDepartment,
  emptyStorageLocation,
  emptyWorkCenter,
  facilitySubtreeCounts,
  fieldDomId,
  hasChildren,
  moved,
  problemsForField,
  type DraftDepartment,
  type DraftFacility,
  type DraftProblem,
  type DraftStorageLocation,
  type DraftWorkCenter,
  type ExistingTree,
  type PlantDraft,
  type SubtreeCounts,
} from "./plant-draft";
import type { WorkCenterTemplateOption } from "./plant-queries";

export interface EditorContext {
  readonly readOnly: boolean;
  readonly templates: readonly WorkCenterTemplateOption[];
  readonly templatesUnavailable: boolean;
  /** Draft problems, so an offending input can carry `aria-invalid`. */
  readonly problems: readonly DraftProblem[];
}

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

function timezoneOptions(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    return intl.supportedValuesOf?.("timeZone") ?? [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// selection model
// ---------------------------------------------------------------------------

export type SelectedNode =
  | { kind: "facility"; facility: DraftFacility; fIndex: number }
  | { kind: "department"; facility: DraftFacility; department: DraftDepartment; dIndex: number }
  | {
      kind: "work_center";
      facility: DraftFacility;
      department: DraftDepartment;
      dIndex: number;
      workCenter: DraftWorkCenter;
      wIndex: number;
    }
  | {
      kind: "storage";
      facility: DraftFacility;
      location: DraftStorageLocation;
      lIndex: number;
    };

/** Find the node a uid names, with its context, or undefined. */
export function findNode(draft: PlantDraft, uid: string | null): SelectedNode | undefined {
  if (uid === null) return undefined;
  for (const [fIndex, facility] of draft.facilities.entries()) {
    if (facility.uid === uid) return { kind: "facility", facility, fIndex };
    for (const [lIndex, location] of facility.storage_locations.entries()) {
      if (location.uid === uid) return { kind: "storage", facility, location, lIndex };
    }
    for (const [dIndex, department] of facility.departments.entries()) {
      if (department.uid === uid) return { kind: "department", facility, department, dIndex };
      for (const [wIndex, workCenter] of department.work_centers.entries()) {
        if (workCenter.uid === uid) {
          return { kind: "work_center", facility, department, dIndex, workCenter, wIndex };
        }
      }
    }
  }
  return undefined;
}

/** The uid that owns a problem field id (`${uid}-code` → uid). */
export function uidOfFieldId(fieldId: string): string {
  return fieldId.replace(/-(?:code|name|tz|address)$/u, "");
}

/** Facility uid an arbitrary node uid lives under, for ancestor expansion. */
export function facilityUidOf(draft: PlantDraft, uid: string): string | undefined {
  const node = findNode(draft, uid);
  return node?.facility.uid;
}

// ---------------------------------------------------------------------------
// committed-tree lookups (draft chips + real ids on committed rows)
// ---------------------------------------------------------------------------

export interface CommittedIndex {
  facilityId: (code: string) => string | undefined;
  departmentId: (facilityCode: string, code: string) => string | undefined;
  workCenterId: (facilityCode: string, code: string) => string | undefined;
}

export function committedIndex(existing: ExistingTree): CommittedIndex {
  const facilities = existing.facilities.filter((f) => f.status === "active");
  const byCode = new Map(facilities.map((f) => [f.code, f]));
  return {
    facilityId: (code) => byCode.get(code.trim())?.id,
    departmentId: (facilityCode, code) => {
      const facility = byCode.get(facilityCode.trim());
      if (facility === undefined) return undefined;
      return existing.departments.find(
        (d) => d.status === "active" && d.facility_id === facility.id && d.code === code.trim(),
      )?.id;
    },
    workCenterId: (facilityCode, code) => {
      const facility = byCode.get(facilityCode.trim());
      if (facility === undefined) return undefined;
      return existing.workCenters.find(
        (w) => w.status === "active" && w.facility_id === facility.id && w.code === code.trim(),
      )?.id;
    },
  };
}

// ---------------------------------------------------------------------------
// per-node inline actions (move / remove, §3.4 + §4 S5)
// ---------------------------------------------------------------------------

function useSubtreeSummary(counts: SubtreeCounts): string {
  const { t } = useTranslation();
  const { term } = useTerm();
  const parts: string[] = [];
  if (counts.departments > 0) {
    parts.push(
      t("onboarding.plant.removeCount.departments", {
        count: counts.departments,
        department: term("department"),
      }),
    );
  }
  if (counts.workCenters > 0) {
    parts.push(
      t("onboarding.plant.removeCount.workCenters", {
        count: counts.workCenters,
        workCenter: term("work_center"),
      }),
    );
  }
  if (counts.storageLocations > 0) {
    parts.push(t("onboarding.plant.removeCount.storage", { count: counts.storageLocations }));
  }
  if (counts.machines > 0) {
    parts.push(
      t("onboarding.plant.removeCount.machines", {
        count: counts.machines,
        machine: term("machine"),
      }),
    );
  }
  return parts.join(", ");
}

function NodeActions({
  index,
  count,
  readOnly,
  removeLabel,
  subtree,
  nodeLabel,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  readOnly: boolean;
  removeLabel: string;
  /** Non-empty ⇒ removing takes children with it, so confirm first (§3.4). */
  subtree?: SubtreeCounts | undefined;
  nodeLabel: string;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const needsConfirm = subtree !== undefined && hasChildren(subtree);
  const summary = useSubtreeSummary(
    subtree ?? { departments: 0, workCenters: 0, storageLocations: 0, machines: 0 },
  );
  if (readOnly) return null;
  return (
    <>
      <TreeRowAction
        label={t("onboarding.plant.moveUp")}
        glyph="↑"
        disabled={index === 0}
        onAct={() => {
          onMove(-1);
        }}
      />
      <TreeRowAction
        label={t("onboarding.plant.moveDown")}
        glyph="↓"
        disabled={index === count - 1}
        onAct={() => {
          onMove(1);
        }}
      />
      <TreeRowAction
        label={removeLabel}
        glyph="✕"
        onAct={() => {
          // An empty node is a one-click undo away; a populated one is not.
          if (needsConfirm) setConfirming(true);
          else onRemove();
        }}
      />
      {confirming ? (
        <WizardDialog
          testId="plant-node-remove-confirm"
          destructive
          title={t("onboarding.plant.removeConfirmTitle", { node: nodeLabel })}
          confirmLabel={t("onboarding.plant.removeConfirmCta")}
          cancelLabel={t("onboarding.plant.cancel")}
          onConfirm={() => {
            setConfirming(false);
            onRemove();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        >
          <p>{t("onboarding.plant.removeConfirmBody", { node: nodeLabel, summary })}</p>
          <p className="plant-hint">{t("onboarding.plant.removeConfirmUndoHint")}</p>
        </WizardDialog>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// the tree
// ---------------------------------------------------------------------------

export interface PlantTreeProps {
  draft: PlantDraft;
  onDraft: (next: PlantDraft) => void;
  ctx: EditorContext;
  committed: CommittedIndex;
  selectedUid: string | null;
  onSelect: (uid: string) => void;
  /** Per-uid expansion overrides; unset rows fall back to `defaultExpanded`. */
  expanded: Readonly<Record<string, boolean>>;
  onToggle: (uid: string) => void;
  defaultExpanded: boolean;
  /** §3.4 removal undo — the screen keeps the previous draft. */
  onRemoved: (label: string, previous: PlantDraft) => void;
}

function isExpanded(
  expanded: Readonly<Record<string, boolean>>,
  uid: string,
  fallback: boolean,
  forced: boolean,
): boolean {
  if (forced) return true;
  return expanded[uid] ?? fallback;
}

export function PlantTree({
  draft,
  onDraft,
  ctx,
  committed,
  selectedUid,
  onSelect,
  expanded,
  onToggle,
  defaultExpanded,
  onRemoved,
}: PlantTreeProps) {
  const { t } = useTranslation();
  const { term } = useTerm();

  const errorUids = new Set(
    ctx.problems.filter((p) => p.severity === "error").map((p) => uidOfFieldId(p.fieldId)),
  );

  const setFacilities = (facilities: DraftFacility[]) => {
    onDraft({ facilities });
  };

  return (
    <ul className="ds-tree" data-testid="plant-tree">
      {draft.facilities.map((facility, fIndex) => {
        const fCommittedId = committed.facilityId(facility.code);
        const fLabel =
          facility.name.trim().length > 0
            ? facility.name
            : t("onboarding.plant.facilityLabel", {
                facility: term("facility"),
                number: fIndex + 1,
              });
        const counts = facilitySubtreeCounts(facility);
        // A facility holding an error opens itself: an error the user cannot
        // see is an error they cannot fix.
        const forced =
          [facility.uid, ...facility.storage_locations.map((l) => l.uid)].some((u) =>
            errorUids.has(u),
          ) ||
          facility.departments.some(
            (d) => errorUids.has(d.uid) || d.work_centers.some((w) => errorUids.has(w.uid)),
          );
        const open = isExpanded(expanded, facility.uid, defaultExpanded, forced);

        const setDepartments = (departments: DraftDepartment[]) => {
          setFacilities(draft.facilities.map((c, i) => (i === fIndex ? { ...c, departments } : c)));
        };
        const setLocations = (storage_locations: DraftStorageLocation[]) => {
          setFacilities(
            draft.facilities.map((c, i) => (i === fIndex ? { ...c, storage_locations } : c)),
          );
        };

        return (
          <TreeNodeRow
            key={facility.uid}
            kind="facility"
            depth={0}
            name={fLabel}
            meta={
              facility.code.trim().length > 0
                ? `${term("facility")} · ${facility.code.trim()} · ${t(
                    "onboarding.plant.facilitySummary",
                    {
                      departments: counts.departments,
                      department: term("department"),
                      workCenters: counts.workCenters,
                      workCenter: term("work_center"),
                    },
                  )}`
                : term("facility")
            }
            selected={selectedUid === facility.uid}
            draftChip={fCommittedId === undefined ? t("onboarding.plant.draftChip") : undefined}
            expanded={open}
            toggleLabel={t("onboarding.plant.toggleNode", { node: fLabel })}
            onToggle={() => {
              onToggle(facility.uid);
            }}
            onSelect={() => {
              onSelect(facility.uid);
            }}
            actions={
              <NodeActions
                index={fIndex}
                count={draft.facilities.length}
                readOnly={ctx.readOnly}
                removeLabel={t("onboarding.plant.removeFacility", {
                  facility: term("facility"),
                })}
                nodeLabel={fLabel}
                subtree={counts}
                onMove={(direction) => {
                  setFacilities(moved(draft.facilities, fIndex, direction));
                }}
                onRemove={() => {
                  onRemoved(fLabel, draft);
                  setFacilities(draft.facilities.filter((_, i) => i !== fIndex));
                }}
              />
            }
            data-testid="plant-facility"
            data-node-id={fCommittedId}
            data-node-path={facility.code.trim()}
          >
            {open ? (
              <ul>
                {facility.departments.map((department, dIndex) => {
                  const dCommittedId = committed.departmentId(facility.code, department.code);
                  const dLabel =
                    department.name.trim().length > 0 ? department.name : term("department");
                  const dCounts = departmentSubtreeCounts(department);
                  const dForced =
                    errorUids.has(department.uid) ||
                    department.work_centers.some((w) => errorUids.has(w.uid));
                  const dOpen = isExpanded(expanded, department.uid, defaultExpanded, dForced);
                  const setWorkCenters = (work_centers: DraftWorkCenter[]) => {
                    setDepartments(
                      facility.departments.map((c, i) =>
                        i === dIndex ? { ...c, work_centers } : c,
                      ),
                    );
                  };
                  return (
                    <TreeNodeRow
                      key={department.uid}
                      kind="department"
                      depth={1}
                      name={dLabel}
                      meta={`${department.code.trim()}${
                        dCounts.workCenters > 0
                          ? ` · ${t("onboarding.plant.departmentSummary", {
                              workCenters: dCounts.workCenters,
                              workCenter: term("work_center"),
                              machines: dCounts.machines,
                              machine: term("machine"),
                            })}`
                          : ""
                      }`}
                      selected={selectedUid === department.uid}
                      draftChip={
                        dCommittedId === undefined ? t("onboarding.plant.draftChip") : undefined
                      }
                      expanded={dOpen}
                      toggleLabel={t("onboarding.plant.toggleNode", { node: dLabel })}
                      onToggle={() => {
                        onToggle(department.uid);
                      }}
                      onSelect={() => {
                        onSelect(department.uid);
                      }}
                      actions={
                        <NodeActions
                          index={dIndex}
                          count={facility.departments.length}
                          readOnly={ctx.readOnly}
                          removeLabel={t("onboarding.plant.removeDepartment", {
                            department: term("department"),
                          })}
                          nodeLabel={dLabel}
                          subtree={dCounts}
                          onMove={(direction) => {
                            setDepartments(moved(facility.departments, dIndex, direction));
                          }}
                          onRemove={() => {
                            onRemoved(dLabel, draft);
                            setDepartments(facility.departments.filter((_, i) => i !== dIndex));
                          }}
                        />
                      }
                      data-testid="plant-department"
                      data-node-id={dCommittedId}
                      data-node-path={`${facility.code.trim()}/${department.code.trim()}`}
                    >
                      {dOpen ? (
                        <ul>
                          {department.work_centers.map((workCenter, wIndex) => {
                            const wCommittedId = committed.workCenterId(
                              facility.code,
                              workCenter.code,
                            );
                            const wLabel =
                              workCenter.name.trim().length > 0
                                ? workCenter.name
                                : term("work_center");
                            return (
                              <TreeNodeRow
                                key={workCenter.uid}
                                kind="work_center"
                                depth={2}
                                name={wLabel}
                                meta={`${workCenter.code.trim()}${
                                  workCenter.machine_count > 0
                                    ? ` · ${t("onboarding.plant.wcMachinesMeta", {
                                        count: workCenter.machine_count,
                                        machine: term("machine"),
                                      })}`
                                    : ""
                                }`}
                                selected={selectedUid === workCenter.uid}
                                draftChip={
                                  wCommittedId === undefined
                                    ? t("onboarding.plant.draftChip")
                                    : undefined
                                }
                                onSelect={() => {
                                  onSelect(workCenter.uid);
                                }}
                                actions={
                                  <NodeActions
                                    index={wIndex}
                                    count={department.work_centers.length}
                                    readOnly={ctx.readOnly}
                                    removeLabel={t("onboarding.plant.removeWorkCenter", {
                                      workCenter: term("work_center"),
                                    })}
                                    nodeLabel={wLabel}
                                    onMove={(direction) => {
                                      setWorkCenters(
                                        moved(department.work_centers, wIndex, direction),
                                      );
                                    }}
                                    onRemove={() => {
                                      onRemoved(wLabel, draft);
                                      setWorkCenters(
                                        department.work_centers.filter((_, i) => i !== wIndex),
                                      );
                                    }}
                                  />
                                }
                                data-testid="plant-work-center"
                                data-node-id={wCommittedId}
                                data-node-path={`${facility.code.trim()}/${department.code.trim()}/${workCenter.code.trim()}`}
                              />
                            );
                          })}
                          {!ctx.readOnly ? (
                            <TreeGhostRow
                              depth={2}
                              data-testid="tree-ghost-work-center"
                              label={t("onboarding.plant.ghostAddWorkCenter", {
                                workCenter: term("work_center"),
                                department: dLabel,
                              })}
                              onAdd={() => {
                                const created = emptyWorkCenter();
                                setWorkCenters([...department.work_centers, created]);
                                onSelect(created.uid);
                              }}
                            />
                          ) : null}
                        </ul>
                      ) : null}
                    </TreeNodeRow>
                  );
                })}
                {!ctx.readOnly ? (
                  <TreeGhostRow
                    depth={1}
                    data-testid="tree-ghost-department"
                    label={t("onboarding.plant.ghostAddDepartment", {
                      department: term("department"),
                      facility: fLabel,
                    })}
                    onAdd={() => {
                      const created = emptyDepartment();
                      setDepartments([...facility.departments, created]);
                      onSelect(created.uid);
                    }}
                  />
                ) : null}
                {facility.storage_locations.map((location, lIndex) => {
                  const lLabel = location.name.trim().length > 0 ? location.name : location.code;
                  return (
                    <TreeNodeRow
                      key={location.uid}
                      kind="storage"
                      depth={1}
                      name={
                        lLabel.trim().length > 0 ? lLabel : t("onboarding.plant.storageHeading")
                      }
                      meta={`${t("onboarding.plant.storageMeta")}${
                        location.code.trim().length > 0 ? ` · ${location.code.trim()}` : ""
                      }${
                        location.parent_code !== null
                          ? ` · ${t("onboarding.plant.parentLabel")} ${location.parent_code}`
                          : ""
                      }`}
                      selected={selectedUid === location.uid}
                      draftChip={t("onboarding.plant.draftChip")}
                      onSelect={() => {
                        onSelect(location.uid);
                      }}
                      actions={
                        <NodeActions
                          index={lIndex}
                          count={facility.storage_locations.length}
                          readOnly={ctx.readOnly}
                          removeLabel={t("onboarding.plant.removeStorage")}
                          nodeLabel={lLabel}
                          onMove={(direction) => {
                            setLocations(moved(facility.storage_locations, lIndex, direction));
                          }}
                          onRemove={() => {
                            onRemoved(lLabel, draft);
                            setLocations(facility.storage_locations.filter((_, i) => i !== lIndex));
                          }}
                        />
                      }
                      data-testid="plant-storage"
                    />
                  );
                })}
                {!ctx.readOnly ? (
                  <TreeGhostRow
                    depth={1}
                    data-testid="tree-ghost-storage"
                    label={t("onboarding.plant.ghostAddStorage", { facility: fLabel })}
                    onAdd={() => {
                      const created = emptyStorageLocation();
                      setLocations([...facility.storage_locations, created]);
                      onSelect(created.uid);
                    }}
                  />
                ) : null}
              </ul>
            ) : null}
          </TreeNodeRow>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// the properties panel for the selection
// ---------------------------------------------------------------------------

function PropField({
  id,
  label,
  value,
  readOnly,
  listId,
  problems,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  readOnly: boolean;
  listId?: string | undefined;
  problems?: readonly DraftProblem[] | undefined;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const invalid = (problems ?? []).length > 0;
  return (
    <div className="ds-fld">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="ds-input"
        value={value}
        readOnly={readOnly}
        list={listId}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${id}-problem` : undefined}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
      {invalid ? (
        <p className="ds-fld-problem" id={`${id}-problem`}>
          {(problems ?? [])
            .map((p) =>
              t(`onboarding.plant.problem.${p.code}`, {
                facility: term("facility"),
                department: term("department"),
                workCenter: term("work_center"),
                machine: term("machine"),
                path: p.path,
              }),
            )
            .join(" ")}
        </p>
      ) : null}
    </div>
  );
}

export interface NodePropertiesProps {
  node: SelectedNode;
  draft: PlantDraft;
  onDraft: (next: PlantDraft) => void;
  ctx: EditorContext;
  committed: CommittedIndex;
  /** §3.4 removal undo — the screen keeps the previous draft. */
  onRemoved: (label: string, previous: PlantDraft) => void;
}

export function NodeProperties({
  node,
  draft,
  onDraft,
  ctx,
  committed,
  onRemoved,
}: NodePropertiesProps) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const zones = timezoneOptions();

  const replaceFacility = (uid: string, next: DraftFacility) => {
    onDraft({ facilities: draft.facilities.map((f) => (f.uid === uid ? next : f)) });
  };

  const title =
    node.kind === "facility"
      ? node.facility.name.trim().length > 0
        ? node.facility.name
        : term("facility")
      : node.kind === "department"
        ? node.department.name.trim().length > 0
          ? node.department.name
          : term("department")
        : node.kind === "work_center"
          ? node.workCenter.name.trim().length > 0
            ? node.workCenter.name
            : term("work_center")
          : node.location.name.trim().length > 0
            ? node.location.name
            : t("onboarding.plant.storageHeading");

  const isDraftNode =
    node.kind === "facility"
      ? committed.facilityId(node.facility.code) === undefined
      : node.kind === "department"
        ? committed.departmentId(node.facility.code, node.department.code) === undefined
        : node.kind === "work_center"
          ? committed.workCenterId(node.facility.code, node.workCenter.code) === undefined
          : true;

  return (
    <PropertiesPanel
      title={title}
      chip={
        isDraftNode ? (
          <span className="ds-chip ds-chip-accent">{t("onboarding.plant.draftChip")}</span>
        ) : undefined
      }
      data-testid="plant-properties"
    >
      {node.kind === "facility" ? (
        <>
          <div className="ds-fld2">
            <PropField
              id={fieldDomId(node.facility.uid, "name")}
              label={t("onboarding.plant.nameLabel")}
              value={node.facility.name}
              readOnly={ctx.readOnly}
              problems={problemsForField(ctx.problems, fieldDomId(node.facility.uid, "name"))}
              onChange={(next) => {
                replaceFacility(node.facility.uid, { ...node.facility, name: next });
              }}
            />
            <PropField
              id={fieldDomId(node.facility.uid, "code")}
              label={t("onboarding.plant.codeLabel")}
              value={node.facility.code}
              readOnly={ctx.readOnly}
              problems={problemsForField(ctx.problems, fieldDomId(node.facility.uid, "code"))}
              onChange={(next) => {
                replaceFacility(node.facility.uid, { ...node.facility, code: next });
              }}
            />
          </div>
          <PropField
            id={fieldDomId(node.facility.uid, "tz")}
            label={t("onboarding.plant.timezoneLabel")}
            value={node.facility.timezone}
            readOnly={ctx.readOnly}
            listId={zones.length > 0 ? "plant-timezone-options" : undefined}
            problems={problemsForField(ctx.problems, fieldDomId(node.facility.uid, "tz"))}
            onChange={(next) => {
              replaceFacility(node.facility.uid, { ...node.facility, timezone: next });
            }}
          />
          <PropField
            id={`${node.facility.uid}-address`}
            label={t("onboarding.plant.addressLabel")}
            value={node.facility.address ?? ""}
            readOnly={ctx.readOnly}
            onChange={(next) => {
              replaceFacility(node.facility.uid, {
                ...node.facility,
                address: next.length > 0 ? next : null,
              });
            }}
          />
          {zones.length > 0 ? (
            <datalist id="plant-timezone-options">
              {zones.map((zone) => (
                <option key={zone} value={zone} />
              ))}
            </datalist>
          ) : null}
          <p className="ds-provenance">
            {t("onboarding.plant.addressHint")} {t("onboarding.plant.codeHint")}
          </p>
        </>
      ) : null}

      {node.kind === "department" ? (
        <>
          <div className="ds-fld2">
            <PropField
              id={fieldDomId(node.department.uid, "name")}
              label={t("onboarding.plant.nameLabel")}
              value={node.department.name}
              readOnly={ctx.readOnly}
              problems={problemsForField(ctx.problems, fieldDomId(node.department.uid, "name"))}
              onChange={(next) => {
                replaceFacility(node.facility.uid, {
                  ...node.facility,
                  departments: node.facility.departments.map((d, i) =>
                    i === node.dIndex ? { ...d, name: next } : d,
                  ),
                });
              }}
            />
            <PropField
              id={fieldDomId(node.department.uid, "code")}
              label={t("onboarding.plant.codeLabel")}
              value={node.department.code}
              readOnly={ctx.readOnly}
              problems={problemsForField(ctx.problems, fieldDomId(node.department.uid, "code"))}
              onChange={(next) => {
                replaceFacility(node.facility.uid, {
                  ...node.facility,
                  departments: node.facility.departments.map((d, i) =>
                    i === node.dIndex ? { ...d, code: next } : d,
                  ),
                });
              }}
            />
          </div>
          {!ctx.readOnly ? (
            <BulkAddWorkCenters
              department={node.department}
              onAdd={(created) => {
                replaceFacility(node.facility.uid, {
                  ...node.facility,
                  departments: node.facility.departments.map((d, i) =>
                    i === node.dIndex ? { ...d, work_centers: [...d.work_centers, ...created] } : d,
                  ),
                });
              }}
            />
          ) : null}
          <p className="ds-provenance">{t("onboarding.plant.codeHint")}</p>
        </>
      ) : null}

      {node.kind === "work_center" ? (
        <>
          <div className="ds-fld2">
            <PropField
              id={fieldDomId(node.workCenter.uid, "name")}
              label={t("onboarding.plant.nameLabel")}
              value={node.workCenter.name}
              readOnly={ctx.readOnly}
              problems={problemsForField(ctx.problems, fieldDomId(node.workCenter.uid, "name"))}
              onChange={(next) => {
                replaceFacility(node.facility.uid, {
                  ...node.facility,
                  departments: node.facility.departments.map((d, i) =>
                    i === node.dIndex
                      ? {
                          ...d,
                          work_centers: d.work_centers.map((w, wi) =>
                            wi === node.wIndex ? { ...w, name: next } : w,
                          ),
                        }
                      : d,
                  ),
                });
              }}
            />
            <PropField
              id={fieldDomId(node.workCenter.uid, "code")}
              label={t("onboarding.plant.codeLabel")}
              value={node.workCenter.code}
              readOnly={ctx.readOnly}
              problems={problemsForField(ctx.problems, fieldDomId(node.workCenter.uid, "code"))}
              onChange={(next) => {
                replaceFacility(node.facility.uid, {
                  ...node.facility,
                  departments: node.facility.departments.map((d, i) =>
                    i === node.dIndex
                      ? {
                          ...d,
                          work_centers: d.work_centers.map((w, wi) =>
                            wi === node.wIndex ? { ...w, code: next } : w,
                          ),
                        }
                      : d,
                  ),
                });
              }}
            />
          </div>
          <div className="ds-fld">
            <label htmlFor={`${node.workCenter.uid}-template`}>
              {t("onboarding.plant.templateLabel")}
            </label>
            <select
              id={`${node.workCenter.uid}-template`}
              className="ds-select"
              value={node.workCenter.template_ref ?? ""}
              disabled={ctx.readOnly}
              onChange={(e) => {
                replaceFacility(node.facility.uid, {
                  ...node.facility,
                  departments: node.facility.departments.map((d, i) =>
                    i === node.dIndex
                      ? {
                          ...d,
                          work_centers: d.work_centers.map((w, wi) =>
                            wi === node.wIndex
                              ? {
                                  ...w,
                                  template_ref: e.target.value.length > 0 ? e.target.value : null,
                                }
                              : w,
                          ),
                        }
                      : d,
                  ),
                });
              }}
            >
              <option value="">{t("onboarding.plant.templateNone")}</option>
              {ctx.templates.map((option) => (
                <option key={option.ref} value={option.ref}>
                  {option.label}
                </option>
              ))}
              {node.workCenter.template_ref !== null &&
              !ctx.templates.some((o) => o.ref === node.workCenter.template_ref) ? (
                <option value={node.workCenter.template_ref}>{node.workCenter.template_ref}</option>
              ) : null}
            </select>
            {ctx.templatesUnavailable ? (
              <p className="ds-fld-problem plant-hint-inline">
                {t("onboarding.plant.templateNoPack")}
              </p>
            ) : null}
          </div>
          <div className="ds-fld">
            <label htmlFor={`${node.workCenter.uid}-machines`}>
              {t("onboarding.plant.machineCountLabel", { machine: term("machine") })}
            </label>
            <input
              id={`${node.workCenter.uid}-machines`}
              className="ds-input"
              type="number"
              min={0}
              step={1}
              value={node.workCenter.machine_count}
              readOnly={ctx.readOnly}
              onChange={(e) => {
                const parsed = Number.parseInt(e.target.value, 10);
                replaceFacility(node.facility.uid, {
                  ...node.facility,
                  departments: node.facility.departments.map((d, i) =>
                    i === node.dIndex
                      ? {
                          ...d,
                          work_centers: d.work_centers.map((w, wi) =>
                            wi === node.wIndex
                              ? {
                                  ...w,
                                  machine_count: Number.isFinite(parsed) ? Math.max(0, parsed) : 0,
                                }
                              : w,
                          ),
                        }
                      : d,
                  ),
                });
              }}
            />
          </div>
          <p className="ds-provenance">{t("onboarding.plant.machineCountDeferred")}</p>
        </>
      ) : null}

      {node.kind === "storage" ? (
        <>
          <div className="ds-fld2">
            <PropField
              id={fieldDomId(node.location.uid, "name")}
              label={t("onboarding.plant.nameLabel")}
              value={node.location.name}
              readOnly={ctx.readOnly}
              problems={problemsForField(ctx.problems, fieldDomId(node.location.uid, "name"))}
              onChange={(next) => {
                replaceFacility(node.facility.uid, {
                  ...node.facility,
                  storage_locations: node.facility.storage_locations.map((l, i) =>
                    i === node.lIndex ? { ...l, name: next } : l,
                  ),
                });
              }}
            />
            <PropField
              id={fieldDomId(node.location.uid, "code")}
              label={t("onboarding.plant.codeLabel")}
              value={node.location.code}
              readOnly={ctx.readOnly}
              problems={problemsForField(ctx.problems, fieldDomId(node.location.uid, "code"))}
              onChange={(next) => {
                replaceFacility(node.facility.uid, {
                  ...node.facility,
                  storage_locations: node.facility.storage_locations.map((l, i) =>
                    i === node.lIndex ? { ...l, code: next } : l,
                  ),
                });
              }}
            />
          </div>
          <div className="ds-fld">
            <label htmlFor={`${node.location.uid}-parent`}>
              {t("onboarding.plant.parentLabel")}
            </label>
            <select
              id={`${node.location.uid}-parent`}
              className="ds-select"
              value={node.location.parent_code ?? ""}
              disabled={ctx.readOnly}
              onChange={(e) => {
                replaceFacility(node.facility.uid, {
                  ...node.facility,
                  storage_locations: node.facility.storage_locations.map((l, i) =>
                    i === node.lIndex
                      ? {
                          ...l,
                          parent_code: e.target.value.length > 0 ? e.target.value : null,
                        }
                      : l,
                  ),
                });
              }}
            >
              <option value="">{t("onboarding.plant.parentNone")}</option>
              {node.facility.storage_locations
                .filter((s) => s.uid !== node.location.uid && s.code.trim().length > 0)
                .map((p) => (
                  <option key={p.uid} value={p.code.trim()}>
                    {p.code.trim()}
                  </option>
                ))}
              {node.location.parent_code !== null &&
              !node.facility.storage_locations.some(
                (p) => p.uid !== node.location.uid && p.code.trim() === node.location.parent_code,
              ) ? (
                <option value={node.location.parent_code}>{node.location.parent_code}</option>
              ) : null}
            </select>
          </div>
          <p className="ds-provenance">{t("onboarding.plant.storageHint")}</p>
        </>
      ) : null}

      {!ctx.readOnly ? (
        <div className="ds-danger">
          <RemoveFromDraft node={node} draft={draft} onDraft={onDraft} onRemoved={onRemoved} />
        </div>
      ) : null}
    </PropertiesPanel>
  );
}

/** The §3.7 red danger link, last in the panel — same confirm rules as the
 * row's ✕ (a populated subtree confirms first, §3.4). */
function RemoveFromDraft({
  node,
  draft,
  onDraft,
  onRemoved,
}: {
  node: SelectedNode;
  draft: PlantDraft;
  onDraft: (next: PlantDraft) => void;
  onRemoved: (label: string, previous: PlantDraft) => void;
}) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const [confirming, setConfirming] = useState(false);

  const label =
    node.kind === "facility"
      ? node.facility.name.trim().length > 0
        ? node.facility.name
        : term("facility")
      : node.kind === "department"
        ? node.department.name.trim().length > 0
          ? node.department.name
          : term("department")
        : node.kind === "work_center"
          ? node.workCenter.name.trim().length > 0
            ? node.workCenter.name
            : term("work_center")
          : node.location.name.trim().length > 0
            ? node.location.name
            : node.location.code;

  const subtree: SubtreeCounts | undefined =
    node.kind === "facility"
      ? facilitySubtreeCounts(node.facility)
      : node.kind === "department"
        ? departmentSubtreeCounts(node.department)
        : undefined;
  const summary = useSubtreeSummary(
    subtree ?? { departments: 0, workCenters: 0, storageLocations: 0, machines: 0 },
  );
  const needsConfirm = subtree !== undefined && hasChildren(subtree);

  function remove(): void {
    onRemoved(label, draft);
    if (node.kind === "facility") {
      onDraft({ facilities: draft.facilities.filter((f) => f.uid !== node.facility.uid) });
      return;
    }
    onDraft({
      facilities: draft.facilities.map((f) => {
        if (f.uid !== node.facility.uid) return f;
        if (node.kind === "department") {
          return { ...f, departments: f.departments.filter((d) => d.uid !== node.department.uid) };
        }
        if (node.kind === "work_center") {
          return {
            ...f,
            departments: f.departments.map((d) =>
              d.uid === node.department.uid
                ? {
                    ...d,
                    work_centers: d.work_centers.filter((w) => w.uid !== node.workCenter.uid),
                  }
                : d,
            ),
          };
        }
        return {
          ...f,
          storage_locations: f.storage_locations.filter((l) => l.uid !== node.location.uid),
        };
      }),
    });
  }

  return (
    <>
      <Button
        data-testid="plant-props-remove"
        onClick={() => {
          if (needsConfirm) setConfirming(true);
          else remove();
        }}
      >
        {t("onboarding.plant.removeFromDraft", { node: label })}
      </Button>
      {confirming ? (
        <WizardDialog
          testId="plant-node-remove-confirm"
          destructive
          title={t("onboarding.plant.removeConfirmTitle", { node: label })}
          confirmLabel={t("onboarding.plant.removeConfirmCta")}
          cancelLabel={t("onboarding.plant.cancel")}
          onConfirm={() => {
            setConfirming(false);
            remove();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        >
          <p>{t("onboarding.plant.removeConfirmBody", { node: label, summary })}</p>
          <p className="plant-hint">{t("onboarding.plant.removeConfirmUndoHint")}</p>
        </WizardDialog>
      ) : null}
    </>
  );
}

/** "Add N work centers" — code-numbered stubs from the department's code, so a
 * 21-work-center plant is a few clicks rather than 21 manual adds (§3.7). */
function BulkAddWorkCenters({
  department,
  onAdd,
}: {
  department: DraftDepartment;
  onAdd: (created: DraftWorkCenter[]) => void;
}) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const [count, setCount] = useState(4);
  const id = `${department.uid}-bulk`;
  return (
    <div className="ds-fld plant-bulk-add">
      <label htmlFor={id}>
        {t("onboarding.plant.bulkAddLabel", { workCenter: term("work_center") })}
      </label>
      <div className="plant-bulk-row">
        <input
          id={id}
          className="ds-input"
          type="number"
          min={1}
          max={50}
          value={count}
          onChange={(e) => {
            const parsed = Number.parseInt(e.target.value, 10);
            setCount(Number.isFinite(parsed) ? Math.min(50, Math.max(1, parsed)) : 1);
          }}
        />
        <Button
          size="sm"
          onClick={() => {
            onAdd(bulkWorkCenters(department.code, count, department.work_centers));
          }}
        >
          {t("onboarding.plant.bulkAddCta", { count })}
        </Button>
      </div>
    </div>
  );
}
